import type { AuthUser } from "../../types/auth";
import { hasPermission } from "../../lib/permissions";
import { FORM_IDS, PROCESS_IDS } from "../../lib/permissionCatalog";

export type ReportId =
  | "requisition-register"
  | "po-register"
  | "vendor-performance"
  | "vendor-statement-of-account"
  | "budget-utilization"
  | "invoice-aging"
  | "paid-awaiting-delivery"
  | "supply-by-port"
  | "asset-register";

export interface ReportNavigationItem {
  id: ReportId;
  label: string;
  to: string;
  groupLabel: string;
}

export interface ReportNavigationSection {
  label: string;
  items: ReportNavigationItem[];
}

const REPORT_NAVIGATION_ITEMS: ReportNavigationItem[] = [
  {
    id: "requisition-register",
    label: "Requisition Register",
    to: "/reports/requisition-register",
    groupLabel: "Purchase Reports",
  },
  {
    id: "po-register",
    label: "PO Register",
    to: "/reports/po-register",
    groupLabel: "Purchase Reports",
  },
  {
    id: "vendor-performance",
    label: "Vendor Performance",
    to: "/reports/vendor-performance",
    groupLabel: "Purchase Reports",
  },
  {
    id: "vendor-statement-of-account",
    label: "SOA",
    to: "/reports/vendor-statement-of-account",
    groupLabel: "Purchase Reports",
  },
  {
    id: "supply-by-port",
    label: "Supply by Port",
    to: "/reports/supply-by-port",
    groupLabel: "Purchase Reports",
  },
  {
    id: "asset-register",
    label: "Stock Asset Register",
    to: "/reports/asset-register",
    groupLabel: "Purchase Reports",
  },
  {
    id: "invoice-aging",
    label: "Invoice Aging",
    to: "/reports/invoice-aging",
    groupLabel: "Finance Reports",
  },
  {
    id: "paid-awaiting-delivery",
    label: "Paid Awaiting Delivery",
    to: "/reports/paid-awaiting-delivery",
    groupLabel: "Finance Reports",
  },
  {
    id: "budget-utilization",
    label: "Budget Utilization",
    to: "/reports/budget-utilization",
    groupLabel: "Budget Reports",
  },
];


export function canSendSupplyByPortMail(user: AuthUser | null | undefined): boolean {
  return hasPermission(user, {
    forms: [FORM_IDS.REPORTS],
    processes: [PROCESS_IDS.REPORT_SUPPLY_BY_PORT_SEND],
  });
}

export function getReportNavigationItems(): ReportNavigationItem[] {
  return REPORT_NAVIGATION_ITEMS;
}

export function canAccessReport(user: AuthUser | null | undefined, reportId: ReportId): boolean {
  if (!user) {
    return false;
  }
  // if (user.type === "crew") {
  //   return false;
  // }

  const reportProcessMap: Record<ReportId, string> = {
    "requisition-register": PROCESS_IDS.REPORT_REQUISITION_REGISTER,
    "po-register": PROCESS_IDS.REPORT_PO_REGISTER,
    "vendor-performance": PROCESS_IDS.REPORT_VENDOR_PERFORMANCE,
    "vendor-statement-of-account": PROCESS_IDS.REPORT_VENDOR_SOA,
    "supply-by-port": PROCESS_IDS.REPORT_SUPPLY_BY_PORT,
    "budget-utilization": PROCESS_IDS.REPORT_BUDGET_UTILIZATION,
    "invoice-aging": PROCESS_IDS.REPORT_INVOICE_AGING,
    "paid-awaiting-delivery": PROCESS_IDS.REPORT_INVOICE_AGING,
    "asset-register": PROCESS_IDS.REPORT_ASSET_REGISTER,
  };

  return hasPermission(user, {
    forms: [FORM_IDS.REPORTS],
    processes: [reportProcessMap[reportId]],
  });
}

export function getAccessibleReportSections(user: AuthUser | null | undefined): ReportNavigationSection[] {
  const sections = new Map<string, ReportNavigationItem[]>();

  for (const item of REPORT_NAVIGATION_ITEMS) {
    if (!canAccessReport(user, item.id)) {
      continue;
    }

    const currentItems = sections.get(item.groupLabel) ?? [];
    currentItems.push(item);
    sections.set(item.groupLabel, currentItems);
  }

  return Array.from(sections.entries()).map(([label, items]) => ({ label, items }));
}
